import { Button, cx } from '@elsa/ui';
import {
  type ComponentProps,
  type CSSProperties,
  type ReactNode,
  useEffect,
  useLayoutEffect,
  useRef,
  useState,
} from 'react';

// Incarcarile Elsei (ELSA-Kit, 08-Loading); clasele sunt in src/loading.css.
//  - PageLoader: ecranul de pornire al aplicatiei, cu pasii reali (sesiunea, firmele, drepturile).
//  - Skeleton*: forma continutului care vine, cu scanarea rosie (tabele, carduri, formulare).
//  - SkeletonChart: barele in val, in locul unui grafic.
//  - Refreshing: banda de sus si continutul estompat, cand datele de pe ecran se reincarca.
//  - Spinner, Radar, InlineLoading, RadarLoading, BusyButton: incarcarile mici.

const HANDLE = 'M29.88 34.12L34.12 29.88L46 41.76V46H41.76Z';
/** Punctele radarului: x, y si intarzierea (s), ca sa se aprinda cand trece baleiajul peste ele. */
const BLIPS: [number, number, number][] = [
  [28.48, 22.32, -2.067],
  [17.23, 23.12, -1.467],
  [9.4, 22.22, -1.133],
  [16.11, 8.75, -0.5],
  [22.61, 16.6, -2.4],
];

/** Radarul: lupa cu baleiaj. Inelele si manerul iau culoarea textului, baleiajul e rosu. */
export function Radar({
  size = 32,
  blips = false,
  className,
}: {
  size?: number;
  blips?: boolean;
  className?: string;
}) {
  return (
    <svg
      className={cx('elsa-radar', className)}
      viewBox="0 0 48 48"
      width={size}
      height={size}
      aria-hidden="true"
    >
      <circle cx="20" cy="20" r="15" fill="none" stroke="currentColor" strokeWidth="4" />
      <circle cx="20" cy="20" r="7.5" fill="none" stroke="currentColor" strokeWidth="2" />
      <g className="sw">
        <path className="ac" d="M20 20L9.61 26.00A12 12 0 0 1 9.61 14.00Z" opacity=".12" />
        <path className="ac" d="M20 20L9.61 14.00A12 12 0 0 1 20.00 8.00Z" opacity=".35" />
        <path className="ac" d="M20 20L20.00 8.00A12 12 0 0 1 30.39 14.00Z" />
      </g>
      {blips
        ? BLIPS.map(([x, y, delay]) => (
            <rect
              key={delay}
              className="bl"
              x={x}
              y={y}
              width="2.4"
              height="2.4"
              fill="currentColor"
              style={{ animationDelay: `${delay}s` }}
            />
          ))
        : null}
      <path d={HANDLE} fill="currentColor" />
    </svg>
  );
}

/** Spinnerul de 16 px (butoane, randuri): un inel gros cu baleiajul rosu. */
export function Spinner({ size = 16, className }: { size?: number; className?: string }) {
  return (
    <svg
      className={cx('elsa-radar', className)}
      viewBox="0 0 48 48"
      width={size}
      height={size}
      aria-hidden="true"
    >
      <circle cx="20" cy="20" r="14.5" fill="none" stroke="currentColor" strokeWidth="5" />
      <g className="sw">
        <path className="ac" d="M20 20L20.00 9.00A11 11 0 0 1 29.53 14.50Z" />
      </g>
      <path d={HANDLE} fill="currentColor" />
    </svg>
  );
}

/** Incarcare pe un rand: spinnerul si textul („Se actualizează datele…”). */
export function InlineLoading({ children = 'Se încarcă…' }: { children?: ReactNode }) {
  return (
    <div role="status" className="flex items-center gap-2.5 text-sm text-gri-800">
      <Spinner className="text-(--elsa-fg)" />
      <span>{children}</span>
    </div>
  );
}

/** Incarcare intr-un loc fara forma cunoscuta (un card mic): radarul animat si textul. */
export function RadarLoading({ children = 'Se încarcă…' }: { children?: ReactNode }) {
  return (
    <div role="status" className="flex flex-col items-center gap-3 py-6 text-sm text-gri-800">
      <Radar size={56} blips className="text-(--elsa-fg)" />
      <span>{children}</span>
    </div>
  );
}

/** Butonul care asteapta un raspuns: dezactivat, cu spinnerul dupa text. */
export function BusyButton({
  busy = false,
  disabled,
  className,
  children,
  ...props
}: ComponentProps<typeof Button> & { busy?: boolean }) {
  return (
    <Button
      {...props}
      disabled={disabled || busy}
      aria-busy={busy || undefined}
      className={cx('aria-busy:cursor-progress aria-busy:opacity-100', className)}
    >
      {children}
      {busy ? <Spinner /> : null}
    </Button>
  );
}

// --- Ecranul de pornire -------------------------------------------------------------------------

/** = --elsa-loader-delay din loading.css: pana atunci ecranul e invizibil (fara clipire). */
const ENTER_MS = 300;
const OUT_MS = 300;
const BOOT_ID = 'elsa-boot';

/** Cat a curs din animatia de intrare a ecranului static din index.html (ms), daca mai e acolo. */
function bootClock(): number | null {
  const t = document.getElementById(BOOT_ID)?.getAnimations()[0]?.currentTime;
  return typeof t === 'number' ? t : null;
}

/**
 * Stinge ecranul static din index.html dupa prima randare (paginile publice). In aplicatie il
 * preia PageLoader, din acelasi punct, deci aici nu mai gaseste nimic.
 */
export function releaseBoot(): void {
  const el = document.getElementById(BOOT_ID);
  if (!el) return;
  const t = bootClock();
  if (t === null || t < ENTER_MS) {
    el.remove();
    return;
  }
  el.classList.add('is-done');
  setTimeout(() => el.remove(), OUT_MS);
}

function paint(pct: HTMLElement | null, fill: HTMLElement | null, value: number): void {
  if (pct) pct.textContent = String(Math.round(value));
  if (fill) fill.style.width = `${value}%`;
}

/**
 * Ecranul de pornire (pagina intreaga). `steps` sunt mesajele pasilor, `step` pasul curent;
 * procentul urca in pasul curent fara sa-l depaseasca, iar la `done` ajunge la 100 si ecranul se
 * stinge. Odata stins nu mai revine, chiar daca `done` redevine fals (alta firma, reincarcari).
 */
export function PageLoader({
  steps,
  step,
  done,
}: {
  steps: readonly string[];
  step: number;
  done: boolean;
}) {
  const [enter] = useState(() => {
    // Preia ecranul static din acelasi punct al intrarii; fara el (navigare in aplicatie), de la
    // montare. Daca termina inainte sa apara, nu se mai arata deloc.
    const t = bootClock();
    const delay = t === null ? ENTER_MS : ENTER_MS - t;
    return { delay, visibleAt: performance.now() + delay };
  });
  const [phase, setPhase] = useState<'run' | 'out' | 'gone'>('run');
  const pct = useRef<HTMLSpanElement>(null);
  const fill = useRef<HTMLDivElement>(null);
  const current = useRef(step);
  current.current = step;
  const finished = useRef(false);

  useLayoutEffect(() => {
    document.getElementById(BOOT_ID)?.remove();
  }, []);

  useEffect(() => {
    const n = steps.length;
    let shown = 0;
    let last = performance.now();
    let raf = 0;
    const frame = (now: number) => {
      if (finished.current) return;
      const dt = now - last;
      last = now;
      const s = Math.min(current.current, n - 1);
      const floor = (s / n) * 100;
      const ceil = ((s + 0.9) / n) * 100;
      // Ajunge repede la inceputul pasului, apoi se apropie incet de capatul lui.
      shown += (ceil - shown) * (1 - Math.exp(-dt / (shown < floor ? 150 : 900)));
      paint(pct.current, fill.current, shown);
      raf = requestAnimationFrame(frame);
    };
    raf = requestAnimationFrame(frame);
    return () => cancelAnimationFrame(raf);
  }, [steps.length]);

  useEffect(() => {
    if (!done || finished.current) return;
    finished.current = true;
    if (performance.now() < enter.visibleAt) {
      setPhase('gone');
      return;
    }
    paint(pct.current, fill.current, 100);
    setPhase('out');
  }, [done, enter]);

  useEffect(() => {
    if (phase !== 'out') return;
    const t = setTimeout(() => setPhase('gone'), OUT_MS);
    return () => clearTimeout(t);
  }, [phase]);

  if (phase === 'gone') return null;
  const message = steps[Math.min(step, steps.length - 1)];
  return (
    <div
      className={cx('elsa-loader', phase === 'out' && 'is-done')}
      role="status"
      aria-live="polite"
      style={{ '--elsa-loader-delay': `${enter.delay}ms` } as CSSProperties}
    >
      <div className="elsa-loader__brand">
        <span className="elsa-loader__word">ELSA</span>
        <span className="elsa-loader__by">
          by Web<b>Custom</b>
        </span>
      </div>
      <div className="elsa-loader__radar">
        <Radar size={240} blips />
      </div>
      <div>
        <div className="elsa-loader__row">
          <div>
            <span className="elsa-loader__kick">Se încarcă</span>
            <span key={message} className="elsa-loader__msg">
              {message}
            </span>
          </div>
          <span className="elsa-loader__pct" aria-hidden="true">
            <span ref={pct}>0</span>
            <small>%</small>
          </span>
        </div>
        <div className="elsa-loader__track" aria-hidden="true">
          <div ref={fill} className="elsa-loader__fill" />
        </div>
      </div>
    </div>
  );
}

// --- Scheletele ---------------------------------------------------------------------------------

/** Latimile barelor (din kit): par randuri reale, nu un model care se repeta vizibil. */
const W = [78, 64, 70, 80, 62, 82, 55, 85, 58, 76, 74, 56, 66, 84, 70, 60, 72];
const width = (i: number) => `${W[i % W.length]}%`;
const range = (n: number) => [...Array(n).keys()];

function Hidden({ children = 'Se încarcă…' }: { children?: string }) {
  return <span className="sr-only">{children}</span>;
}

/** Un tabel care se incarca: randuri cu bare, scanare verticala. Sta direct intr-un Card. */
export function SkeletonTable({ rows = 5, cols = 4 }: { rows?: number; cols?: number }) {
  const grid: CSSProperties = {
    gridTemplateColumns: ['1.4fr', '1.6fr', ...range(Math.max(0, cols - 2)).map(() => '1fr')]
      .slice(0, cols)
      .join(' '),
  };
  return (
    <div role="status" className="elsa-scan elsa-scan--y -mx-4">
      <Hidden />
      {range(rows).map((r) => (
        <div
          key={r}
          style={grid}
          className="grid gap-4 border-b border-gri-300 px-4 py-3.5 last:border-b-0"
        >
          {range(cols).map((c) => (
            <span key={c} className="elsa-sk" style={{ width: width(r * (cols + 1) + c) }} />
          ))}
        </div>
      ))}
    </div>
  );
}

const GRID: Record<number, string> = {
  2: 'sm:grid-cols-2',
  3: 'sm:grid-cols-3',
  4: 'sm:grid-cols-4',
};

/**
 * Carduri care se incarca, cu scanare orizontala: cifre (KPI, `value`), planuri, notificari.
 * `lines` = randurile de text de sub titlu.
 */
export function SkeletonCards({
  count = 3,
  columns = count,
  value = true,
  lines = 0,
}: {
  count?: number;
  columns?: number;
  value?: boolean;
  lines?: number;
}) {
  return (
    <div role="status" className={cx('elsa-scan grid gap-3', GRID[columns])}>
      <Hidden />
      {range(count).map((i) => (
        <div key={i} className={cx('border-2 border-cerneala bg-hartie', value ? 'p-3' : 'p-4')}>
          <span className="elsa-sk" style={{ width: width(i * 4 + 6) }} />
          {value ? (
            <span className="elsa-sk mt-3.5" style={{ height: 22, width: width(i * 4 + 1) }} />
          ) : null}
          {range(lines).map((l) => (
            <span key={l} className="elsa-sk mt-2.5" style={{ width: width(i * 4 + l + 2) }} />
          ))}
        </div>
      ))}
    </div>
  );
}

/** Cateva randuri de text care se incarca (un paragraf, o lista scurta). */
export function SkeletonLines({ lines = 3 }: { lines?: number }) {
  return (
    <div role="status" className="elsa-scan space-y-3 py-1">
      <Hidden />
      {range(lines).map((i) => (
        <span key={i} className="elsa-sk" style={{ width: width(i * 3 + 2) }} />
      ))}
    </div>
  );
}

/** Un formular care se incarca: eticheta si campul, pe doua coloane. */
export function SkeletonForm({ fields = 4 }: { fields?: number }) {
  return (
    <div role="status" className="elsa-scan grid gap-3 sm:grid-cols-2">
      <Hidden />
      {range(fields).map((i) => (
        <div key={i} className="space-y-2">
          <span className="elsa-sk" style={{ height: 10, width: `${24 + ((W[i] ?? 60) % 24)}%` }} />
          <span className="elsa-sk" style={{ height: 34 }} />
        </div>
      ))}
    </div>
  );
}

/** Inaltimile si intarzierile barelor (din kit): valul urca spre bara rosie din dreapta. */
const BARS: [number, number][] = [
  [42, 0],
  [55, 0.09],
  [38, 0.18],
  [61, 0.27],
  [47, 0.36],
  [70, 0.45],
  [52, 0.54],
  [66, 0.63],
  [58, 0.72],
  [74, 0.81],
  [63, 0.9],
  [88, 0.99],
];

/** Un grafic care se incarca: barele in val. */
export function SkeletonChart({ height = 140 }: { height?: number }) {
  return (
    <div role="status" className="elsa-bars mb-3" style={{ height }}>
      <Hidden>Se încarcă graficul…</Hidden>
      {BARS.map(([h, delay]) => (
        <i key={delay} style={{ height: `${h}%`, animationDelay: `${delay}s` }} />
      ))}
    </div>
  );
}

/**
 * Datele de pe ecran se reincarca: banda rosie sus, continutul ramane estompat. In mod implicit
 * banda sta pe marginea de sus a corpului unui Card; `edge="flush"` o pune chiar deasupra.
 */
export function Refreshing({
  active,
  edge = 'card',
  children,
}: {
  active: boolean;
  edge?: 'card' | 'flush';
  children: ReactNode;
}) {
  return (
    <div className="relative" aria-busy={active}>
      {active ? (
        <div
          className={cx(
            'elsa-progress absolute z-10',
            edge === 'card' ? '-inset-x-4 -top-4' : 'inset-x-0 -top-2',
          )}
        />
      ) : null}
      <div className={active ? 'elsa-is-refreshing' : undefined}>{children}</div>
    </div>
  );
}
